/**
 * ConfirmDialog — Neon Dusk confirm modal.
 *
 * Public API frozen — do not change props, default export, or the
 * `confirmColor` union; internal rendering may evolve.
 *
 * Visual contract: blurred dark backdrop, glass panel (radius xl, shadow
 * xl), neutral text cancel button (per DESIGN_SYSTEM.md Voice & Tone;
 * consumers control copy), confirm = <GradientCTA> for 'primary' vs
 * <Button color="error|warning"> for destructive paths.
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Fade,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { forwardRef } from 'react';
import type { Ref } from 'react';
import { colors, designTokens, motion, prefersReducedMotion } from '../../theme/theme';
import GradientCTA from './GradientCTA';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: 'primary' | 'error' | 'warning';
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Fade transition keyed to motion tokens. Reduced-motion users collapse
 * to instant. The MUI CssBaseline @media override neutralises the default
 * transition for users who skip this prop too — this is belt-and-braces.
 */
const FadeTransition = forwardRef(function FadeTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: Ref<unknown>,
) {
  const reduced = prefersReducedMotion();
  const baseMs = parseInt(motion.duration.base, 10) || 250;
  const timeout = reduced ? 0 : baseMs;
  // eslint-disable-next-line xss/no-mixed-html -- React functional component returning JSX (MUI <Fade/>); not an HTML-string sink.
  return (
    <Fade
      ref={ref as Ref<HTMLElement>}
      {...props}
      timeout={{ enter: timeout, exit: timeout }}
      easing={{ enter: motion.easing.enter, exit: motion.easing.exit }}
    />
  );
});

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'primary',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const isDestructive = confirmColor === 'error' || confirmColor === 'warning';

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      TransitionComponent={FadeTransition}
      // Backdrop: dark canvas + blur, per DESIGN_SYSTEM.md §Page Templates.
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: 'rgba(15, 15, 19, 0.7)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          // Glass surface layered over the slightly lighter dark surface so
          // the panel reads cleanly even when the backdrop blur is unsupported.
          bgcolor: colors.background.surface,
          backgroundImage: `linear-gradient(${designTokens.glass.surface}, ${designTokens.glass.surface})`,
          border: `1px solid ${designTokens.glass.border}`,
          borderRadius: `${designTokens.radius.xl}px`,
          backdropFilter: designTokens.glass.blur,
          WebkitBackdropFilter: designTokens.glass.blur,
          boxShadow: designTokens.shadow.xl,
          maxWidth: 440,
          p: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "'Space Grotesk', 'Plus Jakarta Sans', -apple-system, sans-serif",
          fontWeight: 700,
          fontSize: '1.25rem',
          color: '#FFFFFF',
          px: 3,
          pt: 3,
          pb: 1,
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        <DialogContentText
          sx={{
            color: colors.neutral[400],
            fontSize: '0.95rem',
            lineHeight: 1.6,
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <Button
          onClick={onCancel}
          variant="text"
          sx={{
            color: colors.neutral[500],
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: `${designTokens.radius.md}px`,
            px: 2.5,
            py: 1,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.06)',
              color: '#FFFFFF',
            },
            '&:focus-visible': {
              outline: 'none',
              boxShadow: designTokens.shadow.focus,
            },
          }}
        >
          {cancelLabel}
        </Button>
        {isDestructive ? (
          <Button
            onClick={onConfirm}
            variant="contained"
            color={confirmColor}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: `${designTokens.radius.md}px`,
              px: 3,
              py: 1.1,
              boxShadow: 'none',
              transition: `transform ${motion.duration.fast} ${motion.easing.standard}, box-shadow ${motion.duration.base} ${motion.easing.standard}`,
              '&:hover': {
                boxShadow: `0 4px 16px ${
                  confirmColor === 'error'
                    ? 'rgba(251, 113, 133, 0.3)'
                    : 'rgba(251, 191, 36, 0.3)'
                }`,
                transform: 'translateY(-1px)',
              },
              '&:focus-visible': {
                outline: 'none',
                boxShadow: designTokens.shadow.focus,
              },
            }}
          >
            {confirmLabel}
          </Button>
        ) : (
          <GradientCTA size="md" onClick={onConfirm}>
            {confirmLabel}
          </GradientCTA>
        )}
      </DialogActions>
    </Dialog>
  );
}
